// Service Worker Manager
// Handles registration and communication with the service worker

class ServiceWorkerManager {
  constructor() {
    this.registration = null;
    this.isSupported = 'serviceWorker' in navigator;
    this.messageChannels = new Map();
    this.taskCallbacks = new Map();
  }

  // Register the service worker
  async register() {
    if (!this.isSupported) {
      console.warn('Service Workers are not supported in this browser');
      return false;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', this.registration);

      // Handle service worker updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('New service worker available');
            // Optionally notify user about update
          }
        });
      });

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service Worker is ready');
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  // Send message to service worker with response handling
  async sendMessage(type, data, timeout = 30000) {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      throw new Error('Service Worker not available');
    }

    return new Promise((resolve, reject) => {
      const channel = new MessageChannel();
      const taskId = data.taskId || this.generateTaskId();
      
      // Set up timeout
      const timeoutId = setTimeout(() => {
        this.messageChannels.delete(taskId);
        reject(new Error('Service Worker message timeout'));
      }, timeout);

      // Handle responses
      channel.port1.onmessage = (event) => {
        const { type: responseType, taskId: responseTaskId, ...responseData } = event.data;
        
        // Store callback for ongoing tasks
        if (this.taskCallbacks.has(taskId)) {
          this.taskCallbacks.get(taskId)(event.data);
        }

        // Resolve for completion messages
        if (responseType.includes('_COMPLETE') || responseType.includes('_ERROR') || responseType.includes('_TIMEOUT')) {
          clearTimeout(timeoutId);
          this.messageChannels.delete(taskId);
          this.taskCallbacks.delete(taskId);
          
          if (responseType.includes('_ERROR') || responseType.includes('_TIMEOUT')) {
            reject(new Error(responseData.error || 'Task failed'));
          } else {
            resolve(responseData);
          }
        }
      };

      // Store channel for this task
      this.messageChannels.set(taskId, channel);

      // Send message
      navigator.serviceWorker.controller.postMessage(
        { type, data: { ...data, taskId } },
        [channel.port2]
      );
    });
  }

  // Start email verification with progress callback
  async startEmailVerification(apiKey, taskId, progressCallback, options = {}) {
    const { intervalMs = 2000, maxAttempts = 30 } = options;
    
    // Store progress callback
    if (progressCallback) {
      this.taskCallbacks.set(taskId, progressCallback);
    }

    try {
      const result = await this.sendMessage('START_EMAIL_VERIFICATION', {
        taskId,
        apiKey,
        intervalMs,
        maxAttempts
      });
      
      return result;
    } catch (error) {
      this.taskCallbacks.delete(taskId);
      throw error;
    }
  }

  // Stop email verification
  async stopEmailVerification(taskId) {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      return;
    }

    navigator.serviceWorker.controller.postMessage({
      type: 'STOP_EMAIL_VERIFICATION',
      data: { taskId }
    });

    // Clean up callbacks
    this.taskCallbacks.delete(taskId);
    this.messageChannels.delete(taskId);
  }

  // Start website analysis with progress callback
  async startWebsiteAnalysis(websites, config, serviceOptions, scrapingOptions, progressCallback) {
    const taskId = this.generateTaskId();
    
    // Store progress callback
    if (progressCallback) {
      this.taskCallbacks.set(taskId, progressCallback);
    }

    try {
      const result = await this.sendMessage('START_WEBSITE_ANALYSIS', {
        taskId,
        websites,
        config,
        serviceOptions,
        scrapingOptions
      });
      
      return { taskId, ...result };
    } catch (error) {
      this.taskCallbacks.delete(taskId);
      throw error;
    }
  }

  // Stop website analysis
  async stopWebsiteAnalysis(taskId) {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      return;
    }

    navigator.serviceWorker.controller.postMessage({
      type: 'STOP_WEBSITE_ANALYSIS',
      data: { taskId }
    });

    // Clean up callbacks
    this.taskCallbacks.delete(taskId);
    this.messageChannels.delete(taskId);
  }

  // Get task status
  async getTaskStatus(taskId) {
    try {
      const response = await this.sendMessage('GET_TASK_STATUS', { taskId }, 5000);
      return response;
    } catch (error) {
      console.error('Failed to get task status:', error);
      return { status: 'unknown', error: error.message };
    }
  }

  // Get task results from persistent storage
  async getTaskResults(taskId) {
    try {
      const response = await this.sendMessage('GET_TASK_RESULTS', { taskId }, 5000);
      return response;
    } catch (error) {
      console.error('Failed to get task results:', error);
      return { error: error.message };
    }
  }

  // Generate unique task ID
  generateTaskId() {
    return `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Check if service worker is available
  isAvailable() {
    return this.isSupported && navigator.serviceWorker.controller;
  }

  // Get all active tasks
  getActiveTasks() {
    return Array.from(this.taskCallbacks.keys());
  }

  // Clean up all tasks
  cleanup() {
    this.taskCallbacks.clear();
    this.messageChannels.clear();
  }

  // Register for background sync (for offline support)
  async registerBackgroundSync(tag) {
    if (!this.registration || !this.registration.sync) {
      console.warn('Background Sync not supported');
      return false;
    }

    try {
      await this.registration.sync.register(tag);
      console.log(`Background sync registered: ${tag}`);
      return true;
    } catch (error) {
      // Background sync may be disabled in development or certain browsers
      console.warn(`Background sync registration failed for ${tag}:`, error.message);
      return false;
    }
  }

  // Register for periodic background sync (if supported)
  async registerPeriodicSync(tag, minInterval = 24 * 60 * 60 * 1000) {
    if (!this.registration || !this.registration.periodicSync) {
      console.warn('Periodic Background Sync not supported');
      return false;
    }

    try {
      await this.registration.periodicSync.register(tag, {
        minInterval
      });
      console.log(`Periodic sync registered: ${tag}`);
      return true;
    } catch (error) {
      // Periodic sync may be disabled in development or certain browsers
      console.warn(`Periodic sync registration failed for ${tag}:`, error.message);
      return false;
    }
  }
}

// Create singleton instance
const serviceWorkerManager = new ServiceWorkerManager();

// Auto-register when module loads
if (typeof window !== 'undefined') {
  serviceWorkerManager.register().then((success) => {
    if (success) {
      console.log('Service Worker Manager initialized successfully');
      
      // Register background sync for all background tasks
      serviceWorkerManager.registerBackgroundSync('background-sync');
      
      // Register periodic sync for checking pending verifications (every 30 minutes)
      serviceWorkerManager.registerPeriodicSync('check-pending-verifications', 30 * 60 * 1000);
    }
  });
}

export default serviceWorkerManager;